import skrollTop from 'skrollTop';
import {getOffset} from './_getOffset';

export function viewerVertical() {
  // Keep the original slow pan feel: 1 horizontal px is revealed over 2 vertical px.
  const HORIZONTAL_SCROLL_FACTOR = 2;
  const getScrollTop = () => (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
  const getViewportWidth = () => document.documentElement.clientWidth || window.innerWidth;
  const getViewportHeight = () => (window.visualViewport && window.visualViewport.height) || window.innerHeight;
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
  const roundPixel = (num) => Math.round(num * 100) / 100;
  // Smooth path: let CSS `position: sticky` handle the pin/release on the
  // compositor and let JS only write the horizontal `transform`. This removes
  // the per-frame position toggling that makes the parallax lag on iOS Safari.
  // `overflow: clip` is required so the wide image can be clipped WITHOUT the
  // container becoming a scroll container (which would break sticky). When
  // unsupported we use the fixed/absolute JS fallback below.
  const supportsCss = (typeof CSS !== 'undefined' && CSS.supports);
  const supportsSticky = supportsCss && (
    CSS.supports('position', 'sticky') || CSS.supports('position', '-webkit-sticky')
  );
  const supportsOverflowClip = supportsCss && (
    CSS.supports('overflow', 'clip') || CSS.supports('overflow-x', 'clip')
  );
  const useSticky = supportsSticky && supportsOverflowClip;
  const closestGreaterOrEqual = (arr, target) => {
    return arr.reduce((closest, num) => {
      if (num >= target) {
        return (Math.abs(closest - target) > Math.abs(num - target)) ? num : closest;
      }
      return closest;
    }, Infinity);
  }
  const closestLessOrEqual = (arr, target) => {
    return arr.reduce((closest, num) => {
      if (num <= target) {
        return (Math.abs(closest - target) > Math.abs(num - target)) ? num : closest;
      }
      return closest;
    }, -Infinity);
  }

  const viewers = document.querySelectorAll(".viewer-vertical");
  const identity = document.querySelector('.identity')
  if(viewers.length) {
    viewers.forEach((viewer, i) => {
      const viewerid = viewer.dataset.viewer;
      const boxes = viewer.querySelectorAll(".slide");
      const horizontalBoxes = viewer.querySelectorAll(".slide-horizontal");
      const pageNumber = document.querySelectorAll(`.slide-counter[data-viewer="${viewerid}"]`);
      const prevButton = document.querySelectorAll(`.slide-prev[data-viewer="${viewerid}"]`);
      const nextButton = document.querySelectorAll(`.slide-next[data-viewer="${viewerid}"]`);
      const totalBoxes = boxes.length;
      let viewerOffsetTop = 0;
      let viewerOffsetBottom = 0;
      let currentBox = 0;
      let renderedBox = -1;
      let lastAtEnd = null;
      let boxPosArr = [];
      let scrollPosArr = [];
      // Per-box callbacks collected so a single rAF-throttled scroll/resize/load
      // handler can drive them all (instead of one listener per box).
      const horizontalViews = [];
      const horizontalResizers = [];

      if (horizontalBoxes.length) {
        horizontalBoxes.forEach((box, i) => {
          box.classList.remove("box-overflow");
          const img = box.querySelector("img");
          if (!img) return;
          let isReverse = false;
          if(box.classList.contains('is-reverse')) {
            isReverse = true;
          }
          const useStickyForBox = useSticky && !isReverse;
          if (useStickyForBox) {
            viewer.classList.add('is-sticky-viewer');
            box.classList.add('is-sticky-horizontal');
          } else {
            box.classList.remove('is-sticky-horizontal');
          }

          let ww = 0;
          let wh = 0;
          let h = 0;
          let imgw = 0;
          let offsetTop = 0;
          let offsetBottom = 0;
          let gap = 0;
          let scrollDistance = 0;
          let lastInlineState = null;
          let lastTransformX = null;

          const resetHorizontalImageStyles = () => {
            img.style.position = ``;
            img.style.top = ``;
            img.style.bottom = ``;
            img.style.left = ``;
            img.style.right = ``;
            img.style.transform = ``;
            lastInlineState = null;
            lastTransformX = null;
          };

          const setInlineState = (state) => {
            if (lastInlineState === state) return;

            if (state === 'fixed') {
              img.style.position = `fixed`;
              img.style.top = `0`;
              img.style.bottom = ``;
              img.style.left = isReverse ? `` : `0`;
              img.style.right = isReverse ? `0` : ``;
            } else if (state === 'absolute') {
              img.style.position = `absolute`;
              img.style.top = ``;
              img.style.bottom = `0`;
              img.style.left = isReverse ? `` : `0`;
              img.style.right = isReverse ? `0` : ``;
            } else {
              img.style.position = ``;
              img.style.top = ``;
              img.style.bottom = ``;
              img.style.left = ``;
              img.style.right = ``;
              if (state === 'reset') {
                img.style.transform = ``;
                lastTransformX = null;
              }
            }

            lastInlineState = state;
          };

          const setHorizontalTransform = (x) => {
            const nextX = roundPixel(clamp(x, 0, gap));
            if (lastTransformX === nextX) return;

            img.style.transform = isReverse
              ? `translate3d(${nextX}px, 0, 0)`
              : `translate3d(-${nextX}px, 0, 0)`;
            lastTransformX = nextX;
          };

          const getImageRatio = () => {
            const naturalWidth = img.naturalWidth;
            const naturalHeight = img.naturalHeight;
            if (naturalWidth > 0 && naturalHeight > 0) return naturalWidth / naturalHeight;

            const attrWidth = parseFloat(img.getAttribute('width'));
            const attrHeight = parseFloat(img.getAttribute('height'));
            if (attrWidth > 0 && attrHeight > 0) return attrWidth / attrHeight;

            return 0;
          };

          const getRenderedImageSize = () => {
            const rect = img.getBoundingClientRect();
            const height = rect.height || img.clientHeight || box.clientHeight || getViewportHeight();
            const ratio = getImageRatio();
            const width = (height > 0 && ratio > 0) ? height * ratio : (rect.width || img.clientWidth);
            return {
              width: width || 0,
              height: height || 0
            };
          };

          const setHorizontalSize = () => {
            box.style.height = ``;
            box.classList.remove('horizontal-scrolling');
            box.setAttribute('data-bottom', "");
            resetHorizontalImageStyles();

            ww = getViewportWidth();
            wh = getViewportHeight();
            const imgSize = getRenderedImageSize();
            h = box.clientHeight || imgSize.height || wh;
            imgw = imgSize.width;
            if (ww < imgw) {
              box.classList.add('horizontal-scrolling');
              gap = Math.ceil(imgw - ww);
              scrollDistance = Math.ceil(gap * HORIZONTAL_SCROLL_FACTOR);
              const maxEdgeHold = Math.max(48, Math.min(180, wh * 0.2));
              const edgeHold = Math.ceil(clamp(gap * 0.25, 48, maxEdgeHold));
              box.style.height = `${Math.ceil(h) + scrollDistance + edgeHold}px`;
              h = box.clientHeight;
              const offset = getOffset(box);
              offsetTop = offset.top;
              offsetBottom = offsetTop + h;
              const horizontalEnd = offsetTop + scrollDistance;
              box.setAttribute('data-bottom', Math.round(horizontalEnd));
            } else {
              box.classList.remove('horizontal-scrolling');
              box.setAttribute('data-bottom', "");
              gap = 0;
              scrollDistance = 0;
            }
          };

          const horizontalView = (scTop) => {
            if(box.classList.contains('horizontal-scrolling')) {
              if (scTop === undefined) scTop = getScrollTop();
              const progress = scrollDistance > 0 ? clamp((scTop - offsetTop) / scrollDistance, 0, 1) : 0;
              const x = progress * gap;
              if (useStickyForBox) {
                setInlineState('sticky');
                setHorizontalTransform(x);
                return;
              }
              // Read innerHeight live: iOS Safari changes it as the toolbar
              // shows/hides during scroll, which would otherwise leave the
              // fixed->absolute switch point computed from a stale height.
              const scBottom = scTop + getViewportHeight();
              if (scTop >= offsetTop) {
                if (scBottom >= offsetBottom) {
                  setInlineState('absolute');
                  setHorizontalTransform(gap);
                } else {
                  setInlineState('fixed');
                  setHorizontalTransform(x);
                }
              } else {
                setInlineState('reset');
              }
            } else {
              setInlineState('reset');
            }
          };
      
          // Collect this box's measure+position callbacks. A single
          // viewer-level rAF-throttled scroll/resize/load handler drives them,
          // instead of registering one scroll listener per box.
          const resize = () => { setHorizontalSize(); horizontalView(); };
          horizontalResizers.push(resize);
          horizontalViews.push(horizontalView);

          // Each lazy image may finish loading at a different time; re-measure
          // that box and refresh page positions when it does. This keeps the
          // horizontal-scrolling decision robust against image-load timing
          // (the iOS Safari "works for some, not others" race).
          if (img && !img.complete) {
            img.addEventListener("load", () => {
              resize();
              setPos();
              checkScrollTopPos();
            });
          }

          setHorizontalSize();
          horizontalView();
        });
      }

      const setPos = () => {
        boxPosArr = [];
        scrollPosArr = [];
        const beforeStr = getComputedStyle(viewer, ":before").content;
        // console.log(beforeStr);
        let headerHeight = 0;
        if(identity && beforeStr == '"sp"') {
          headerHeight = identity.offsetHeight
        }
        boxes.forEach((box, i) => {
          const boxScrollOffset = getOffset(box);
          let boxScrollTop = boxScrollOffset.top
          boxScrollTop = boxScrollOffset.top - headerHeight;
          const boxScrollBottom = boxScrollTop + box.offsetHeight;
          boxPosArr.push([boxScrollTop, boxScrollBottom]);
          scrollPosArr.push(boxScrollTop);
          if(box.classList.contains('horizontal-scrolling')) {
            scrollPosArr.push(Number(box.dataset.bottom));
          }
        })
        const viewerOffset = getOffset(viewer);
        viewerOffsetTop = viewerOffset.top;
        const viewerHeight = viewer.offsetHeight;
        viewerOffsetBottom = viewerOffsetTop + viewerHeight;
      }

      const checkScrollTopPos = function(scTop) {
        if (scTop === undefined) scTop = getScrollTop();
        const scBottom = scTop + getViewportHeight();

        // Check if viewer-vertical bottom is above window bottom.
        // Only touch the class when the state actually changes.
        const atEnd = viewerOffsetBottom < scBottom;
        if (atEnd !== lastAtEnd) {
          document.body.classList.toggle('is-vertical-viewer-bottom-end', atEnd);
          lastAtEnd = atEnd;
        }

        boxPosArr.forEach((arr, i) => {
          const thisScrollTop = arr[0];
          const thisScrollBottom = arr[1];
          if(thisScrollTop < scTop && thisScrollBottom > scTop) {
            currentBox = i;
          }
        })
        // Write the counter only when the current page changed.
        if (pageNumber.length && currentBox !== renderedBox) {
          renderedBox = currentBox;
          pageNumber.forEach((number) => {
            number.textContent = `${currentBox + 1}/${totalBoxes}`;
          });
        }
      }

      const goToPage = (dir) => {
        const scTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
        const dur = 600;
        if(dir == 'prev') {
          let top = closestLessOrEqual(scrollPosArr, scTop - 1);
          if(top == -Infinity) {
            top = viewerOffsetTop
          }
          document.body.classList.remove('is-disable-scroll-direction');
          skrollTop.scrollTo({
            to: top,
            duration: dur,
            callback: function() {}
          });
        } else if(dir == 'next') {
          let top = closestGreaterOrEqual(scrollPosArr, scTop + 1);
          if(top == Infinity) {
            top = viewerOffsetBottom;
          }
          document.body.classList.add('is-disable-scroll-direction');
          skrollTop.scrollTo({
            to: top,
            duration: dur,
            callback: function() {
              document.body.classList.add('dir-bottom');
            }
          });
          setTimeout(() => {
            document.body.classList.remove('is-disable-scroll-direction');
          }, dur+300)
        }
      }

      if (prevButton.length) {
        prevButton.forEach((button, i) => {
          button.addEventListener("click", (e) => {
            e.preventDefault();
            if(!document.body.classList.contains('is-disable-scroll-direction')) {
              goToPage('prev')
            }
          });
        });
      }

      if (nextButton.length) {
        nextButton.forEach((button, i) => {
          button.addEventListener("click", (e) => {
            e.preventDefault();
            if(!document.body.classList.contains('is-disable-scroll-direction')) {
              goToPage('next')
            }
          });
        });
      }

      document.addEventListener("keydown", (e) => {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          if(!document.body.classList.contains('is-disable-scroll-direction')) {
            goToPage('prev');
          }
        } else if (e.key === "ArrowDown" || e.key === " ") {
          e.preventDefault();
          if(!document.body.classList.contains('is-disable-scroll-direction')) {
            goToPage('next');
          }
        }
      })

      // Single rAF-throttled scroll handler: reads scrollTop once per frame and
      // drives the horizontal parallax for every box plus the page counter.
      // This replaces the previous per-box + per-viewer scroll listeners and
      // avoids layout thrashing during scroll.
      let ticking = false;
      window.addEventListener("scroll", () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const scTop = getScrollTop();
          for (let j = 0; j < horizontalViews.length; j++) horizontalViews[j](scTop);
          checkScrollTopPos(scTop);
          ticking = false;
        });
      }, { passive: true });

      const recalc = () => {
        // Re-measure horizontal boxes first (their height grows by gap*2),
        // then recompute page positions from the new layout.
        for (let j = 0; j < horizontalResizers.length; j++) horizontalResizers[j]();
        setPos();
        checkScrollTopPos();
      };

      let lastInnerWidth = getViewportWidth();
      window.addEventListener("resize", () => {
        const innerWidth = getViewportWidth();
        if (lastInnerWidth != innerWidth) {
          lastInnerWidth = innerWidth;
          recalc();
        }
      }, { passive: true });

      window.addEventListener("load", recalc);
      window.addEventListener("pageshow", recalc);
      window.addEventListener("orientationchange", recalc);

      setPos();
      checkScrollTopPos();
    });
  }
}
